import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaCheckSquare, FaRegSquare, FaLock, FaInfoCircle } from 'react-icons/fa';
import instanciaAxios from '../../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../SpinnerCargando';
import FuncionesProcesoNotasFactura from '../../venta/Funciones/FuncionesProcesoNotasFactura';
import FuncionesGenerales from '../FuncionesGenerales';
import ModalPDFBase64 from '../ModalPDFBase64';
import ModalConfirmacion from '../ModalConfirmacion';

function crearClaveIdempotenciaFiscal(prefijo = 'fiscal') {
  if (window?.crypto?.randomUUID) {
    return `${prefijo}-${window.crypto.randomUUID()}`;
  }
  return `${prefijo}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * CÓDIGOS DE MODIFICACIÓN DGII:
 * 1 = Anula el NCF modificado (100% automático, sin edición)
 * 2 = Corrige Texto (solo editar descripción, montos igual)
 * 3 = Corrige Montos (editar cantidad y monto de ajuste)
 * 4 = Reemplazo NCF Contingencia (100% automático)
 * 5 = Referencia Factura Consumo (flexible como 3)
 */

// Configuración de comportamiento por código de modificación
const COMPORTAMIENTO_CODIGO = {
  1: { // Anula NCF
    autoSeleccionarTodo: true,
    permitirEdicion: false,
    permitirSeleccion: false,
    mostrarOpcionImpuestos: true,
    descripcion: 'Se anulará completamente la factura original'
  },
  2: { // Corrige Texto
    autoSeleccionarTodo: false,
    permitirEdicion: 'descripcion', // solo descripción
    permitirSeleccion: true,
    mostrarOpcionImpuestos: false,
    montoCero: true, // No afecta balance - monto = 0
    descripcion: 'Corrige solo la descripción de productos (monto $0, no afecta balance)'
  },
  3: { // Corrige Montos
    autoSeleccionarTodo: false,
    permitirEdicion: 'montos', // cantidad y monto
    permitirSeleccion: true,
    mostrarOpcionImpuestos: true,
    descripcion: 'Permite ajustar cantidades y montos'
  },
  4: { // Reemplazo Contingencia
    autoSeleccionarTodo: true,
    permitirEdicion: false,
    permitirSeleccion: false,
    mostrarOpcionImpuestos: true,
    descripcion: 'Reemplazo de NCF emitido en contingencia'
  },
  5: { // Referencia Factura Consumo
    autoSeleccionarTodo: false,
    permitirEdicion: 'montos',
    permitirSeleccion: true,
    mostrarOpcionImpuestos: true,
    descripcion: 'Referencia a factura de consumo electrónica'
  }
};

/**
 * Modal para crear Notas de Credito (E34) o Notas de Debito (E33) desde una factura
 * Con semántica correcta según código de modificación DGII
 */
const ModalCrearNotaFactura = ({
  mostrar,
  onClose,
  facturaOriginal,
  tipoNota = 'credito',
  tipoOrigen = 'factura', // 'factura' (E31/E32) o 'compra' (E41 informal)
  onExito = () => {}
}) => {
  const { formatoMoneda } = FuncionesGenerales();
  const { obtenerCodigosModificacion } = FuncionesProcesoNotasFactura();
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    codigo_modificacion: '',
    razon_modificacion: '',
    comentario: '',
    incluir_impuestos: true // Nueva opción para impuestos
  });
  
  // Lineas de la factura original con seleccion y valores editables
  const [lineasFactura, setLineasFactura] = useState([]);
  
  const [cargando, setCargando] = useState(false);
  const [errores, setErrores] = useState({});
  
  // Estados para PDF
  const [pdfBase64, setPdfBase64] = useState(null);
  const [mostrarPDF, setMostrarPDF] = useState(false);
  const [respuestaData, setRespuestaData] = useState(null);
  
  // Estados para confirmación de serie B
  const [mostrarConfirmacionSerieB, setMostrarConfirmacionSerieB] = useState(false);
  const [payloadPendiente, setPayloadPendiente] = useState(null);

  // Codigos de modificacion segun tipo de nota
  const codigosModificacion = obtenerCodigosModificacion().filter(codigo => {
    // Código 5 (Referencia Factura Consumo E32) no aplica a compras E41
    if (tipoOrigen === 'compra' && codigo.codigo === 5) return false;
    if (tipoNota === 'credito') return codigo.aplica_nc;
    if (tipoNota === 'debito') return codigo.aplica_nd;
    return true;
  });

  // Obtener comportamiento actual según código seleccionado
  const codigoActual = parseInt(formData.codigo_modificacion) || 0;
  const comportamiento = COMPORTAMIENTO_CODIGO[codigoActual] || {
    autoSeleccionarTodo: false,
    permitirEdicion: 'montos',
    permitirSeleccion: true,
    mostrarOpcionImpuestos: false
  };

  // Cargar lineas de factura cuando se abre el modal
  useEffect(() => {
    if (mostrar && facturaOriginal) {
      // Resetear formulario
      setFormData({
        codigo_modificacion: '',
        razon_modificacion: '',
        comentario: '',
        incluir_impuestos: true
      });
      setErrores({});
      setPdfBase64(null);
      setMostrarPDF(false);
      setRespuestaData(null);
      
      // Cargar lineas de la factura con campos editables
      if (facturaOriginal.detalles_factura && facturaOriginal.detalles_factura.length > 0) {
        setLineasFactura(facturaOriginal.detalles_factura.map((d, index) => ({
          id: d.id_detalle_factura || index,
          id_producto: d.id_producto,
          descripcion_producto: d.descripcion || d.descripcion_producto || 'Producto',
          descripcionOriginal: d.descripcion || d.descripcion_producto || 'Producto',
          cantidadOriginal: parseFloat(d.cantidad) || 1,
          precioOriginal: parseFloat(d.precio) || 0,
          // FIX: Usar null check para permitir itbis_tasa = 0 (productos exentos)
          itbis_tasa: d.itbis_tasa != null ? parseInt(d.itbis_tasa) : 18,
          indicador_facturacion: d.indicador_facturacion || 1,
          indicador_bien_servicio: d.id_tipo_producto || d.indicador_bien_servicio || 2,
          descuento: parseFloat(d.descuento) || 0,
          // Campos editables para la nota
          seleccionado: false,
          cantidadNota: parseFloat(d.cantidad) || 1,
          precioNota: parseFloat(d.precio) || 0,
          descripcionNota: d.descripcion || d.descripcion_producto || 'Producto'
        })));
      } else {
        setLineasFactura([]);
      }
    }
  }, [mostrar, facturaOriginal]);

  // Efecto para auto-seleccionar según código de modificación
  useEffect(() => {
    if (codigoActual && comportamiento.autoSeleccionarTodo) {
      // Auto-seleccionar todas las líneas con valores originales
      setLineasFactura(prev => prev.map(linea => ({
        ...linea,
        seleccionado: true,
        cantidadNota: linea.cantidadOriginal,
        precioNota: linea.precioOriginal,
        descripcionNota: linea.descripcionOriginal
      })));
    } else if (codigoActual && !comportamiento.autoSeleccionarTodo) {
      // Resetear valores a originales cuando cambia código
      setLineasFactura(prev => prev.map(linea => ({
        ...linea,
        cantidadNota: linea.cantidadOriginal,
        precioNota: linea.precioOriginal,
        descripcionNota: linea.descripcionOriginal
      })));
    }
  }, [codigoActual]);

  // Cerrar modal con Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mostrar && !mostrarPDF) {
        onClose();
      }
    };

    if (mostrar) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [mostrar, mostrarPDF, onClose]);

  // Manejar cambio en campos del formulario
  const handleFormChange = (campo, valor) => {
    setFormData(prev => ({ ...prev, [campo]: valor }));
    if (errores[campo]) {
      setErrores(prev => ({ ...prev, [campo]: null }));
    }
  };

  // Toggle seleccion de linea (solo si permitido)
  const handleToggleLinea = (index) => {
    if (!comportamiento.permitirSeleccion) return;
    
    setLineasFactura(prev => prev.map((linea, i) => 
      i === index ? { ...linea, seleccionado: !linea.seleccionado } : linea
    ));
    if (errores.lineas) {
      setErrores(prev => ({ ...prev, lineas: null }));
    }
  };

  // Seleccionar/deseleccionar todas (solo si permitido)
  const handleSelectAll = () => {
    if (!comportamiento.permitirSeleccion) return;
    
    const todasSeleccionadas = lineasFactura.every(l => l.seleccionado);
    setLineasFactura(prev => prev.map(linea => ({
      ...linea,
      seleccionado: !todasSeleccionadas
    })));
    if (errores.lineas) {
      setErrores(prev => ({ ...prev, lineas: null }));
    }
  };

  // Cambiar cantidad de una linea (solo código 3 y 5)
  const handleCantidadChange = (index, valor) => {
    if (comportamiento.permitirEdicion !== 'montos') return;
    
    const nuevaCantidad = parseFloat(valor) || 0;
    setLineasFactura(prev => prev.map((linea, i) => {
      if (i === index) {
        // No puede exceder la cantidad original
        const cantidadFinal = Math.min(nuevaCantidad, linea.cantidadOriginal);
        return { ...linea, cantidadNota: cantidadFinal > 0 ? cantidadFinal : 0.01 };
      }
      return linea;
    }));
  };

  // Cambiar monto/precio de una linea (solo código 3 y 5)
  const handleMontoChange = (index, valor) => {
    if (comportamiento.permitirEdicion !== 'montos') return;
    
    const nuevoMonto = parseFloat(valor) || 0;
    setLineasFactura(prev => prev.map((linea, i) => {
      if (i === index) {
        // Para NC: el monto no puede exceder el monto original
        const montoFinal = tipoNota === 'credito' 
          ? Math.min(nuevoMonto, linea.precioOriginal)
          : nuevoMonto;
        return { ...linea, precioNota: montoFinal >= 0 ? montoFinal : 0 };
      }
      return linea;
    }));
  };

  // Cambiar descripción de una linea (solo código 2)
  const handleDescripcionChange = (index, valor) => {
    if (comportamiento.permitirEdicion !== 'descripcion') return;
    
    setLineasFactura(prev => prev.map((linea, i) => 
      i === index ? { ...linea, descripcionNota: valor } : linea
    ));
  };

  // Calcular total de una linea para la nota
  const calcularTotalLineaNota = (linea) => {
    if (!linea.seleccionado) return 0;
    // Código 2 (Corrige Texto): monto = 0, no afecta balance
    if (comportamiento.montoCero) return 0;
    const subtotal = linea.cantidadNota * linea.precioNota;
    const itbis = formData.incluir_impuestos ? subtotal * (linea.itbis_tasa / 100) : 0;
    return subtotal + itbis;
  };

  // Calcular totales de la nota
  const calcularTotales = () => {
    // Código 2 (Corrige Texto): monto = 0, no afecta balance
    if (comportamiento.montoCero) {
      return { monto: 0, impuesto_total: 0, monto_total: 0 };
    }

    const lineasSeleccionadas = lineasFactura.filter(l => l.seleccionado);
    
    let monto = 0;
    let impuesto_total = 0;
    
    lineasSeleccionadas.forEach(linea => {
      const subtotal = linea.cantidadNota * linea.precioNota;
      const itbis = formData.incluir_impuestos ? subtotal * (linea.itbis_tasa / 100) : 0;
      monto += subtotal;
      impuesto_total += itbis;
    });
    
    return {
      monto: parseFloat(monto.toFixed(2)),
      impuesto_total: parseFloat(impuesto_total.toFixed(2)),
      monto_total: parseFloat((monto + impuesto_total).toFixed(2))
    };
  };

  const totales = calcularTotales();
  const lineasSeleccionadas = lineasFactura.filter(l => l.seleccionado);

  // Balance disponible de la factura (solo aplica para NC, excepto código 2)
  const balanceDisponible = parseFloat(facturaOriginal?.balance) || 0;
  
  // Usar tolerancia para comparación de punto flotante (0.01 = 1 centavo)
  const EPSILON = 0.01;
  const excedeMonto = tipoNota === 'credito' && !comportamiento.montoCero && (totales.monto_total - balanceDisponible) > EPSILON;

  // Validar formulario
  const validarFormulario = () => {
    const nuevosErrores = {};
    
    if (!formData.codigo_modificacion) {
      nuevosErrores.codigo_modificacion = 'Seleccione la razón de modificación';
    }
    
    if (lineasSeleccionadas.length === 0) {
      nuevosErrores.lineas = 'Seleccione al menos una línea';
      toast.warning('Debe seleccionar al menos una línea de la factura');
    }

    // Validar que el monto no exceda el balance disponible (solo para NC, excepto código 2)
    // Usar tolerancia de 1 centavo para comparaciones de punto flotante
    const EPSILON = 0.01;
    if (tipoNota === 'credito' && !comportamiento.montoCero) {
      if (balanceDisponible <= 0) {
        nuevosErrores.balance = 'La factura no tiene balance disponible';
        toast.error('Esta factura no tiene balance disponible para aplicar nota de crédito');
      } else if ((totales.monto_total - balanceDisponible) > EPSILON) {
        nuevosErrores.balance = 'El monto excede el balance disponible';
        toast.error(`El monto de la nota (${formatoMoneda(totales.monto_total)}) excede el balance disponible (${formatoMoneda(balanceDisponible)})`);
      }
    }

    // Validar que el monto sea mayor a cero (excepto código 2 que es monto 0)
    if (totales.monto_total <= 0 && !comportamiento.montoCero) {
      nuevosErrores.monto = 'El monto debe ser mayor a cero';
      toast.warning('El monto de la nota debe ser mayor a cero');
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Enviar nota
  const handleSubmit = async () => {
    if (!validarFormulario()) return;

    setCargando(true);
    try {
      const endpoint = tipoNota === 'credito' 
        ? '/nota_credito_factura' 
        : '/nota_debito_factura';
      const claveIdempotencia = crearClaveIdempotenciaFiscal(`nota-${tipoNota}`);

      const payload = {
        ...(tipoOrigen === 'compra'
          ? { id_compra: facturaOriginal.id_compra }
          : { id_factura: facturaOriginal.id_factura }),
        codigo_modificacion: parseInt(formData.codigo_modificacion),
        razon_modificacion: formData.razon_modificacion || null,
        monto: totales.monto,
        impuesto_total: totales.impuesto_total,
        monto_total: totales.monto_total,
        incluir_impuestos: formData.incluir_impuestos,
        comentario: formData.comentario || null,
        clave_idempotencia: claveIdempotencia,
        detalles: lineasSeleccionadas.map(linea => ({
          id_producto: linea.id_producto || null,
          descripcion_producto: linea.descripcionNota || linea.descripcion_producto,
          cantidad: linea.cantidadNota,
          precio: linea.precioNota,
          descuento: 0,
          itbis_tasa: formData.incluir_impuestos ? linea.itbis_tasa : 0,
          indicador_facturacion: linea.indicador_facturacion || 1,
          indicador_bien_servicio: linea.indicador_bien_servicio || 2
        }))
      };

      const respuesta = await instanciaAxios.post(endpoint, payload, {
        headers: { 'x-idempotency-key': claveIdempotencia }
      });

      if (respuesta.status === 201 || respuesta.status === 200) {
        const tipoTexto = tipoNota === 'credito' ? 'Crédito' : 'Débito';
        const esContingencia = respuesta.data?.contingencia || respuesta.data?.data?.dgii_estado === 'PendienteEnvio';
        
        // Detectar fallback a serie B
        if (esContingencia) {
          toast.warning(
            `⚠️ Nota de ${tipoTexto} creada con envío DGII pendiente. NCF: ${respuesta.data.data?.ncf}`,
            { autoClose: 9000 }
          );
        } else if (respuesta.data.mensaje && respuesta.data.mensaje.includes('Serie B')) {
          toast.warning(
            `⚠️ ${respuesta.data.mensaje}\n\nNota de ${tipoTexto} con NCF físico: ${respuesta.data.data?.ncf}`,
            { autoClose: 10000 }
          );
        } else {
          toast.success(`Nota de ${tipoTexto} creada exitosamente`);
        }
        
        // Guardar datos de respuesta
        setRespuestaData(respuesta.data?.data);
        
        // Si hay PDF en la respuesta, mostrarlo
        if (respuesta.data?.data?.pdf?.pdf_base64) {
          setPdfBase64(respuesta.data.data.pdf.pdf_base64);
          setMostrarPDF(true);
        } else {
          // Sin PDF, cerrar modal normalmente
          onExito(respuesta.data);
          onClose();
        }
      }
    } catch (error) {
      console.error('Error al crear nota:', error);
      
      // Caso especial: Error de secuencias NCF (status 409)
      if (error.response?.status === 409) {
        const codigo = error.response?.data?.codigo;
        const puedeUsarB = error.response?.data?.puedeUsarB;
        
        // No hay serie E pero hay serie B disponible → mostrar confirmación
        if (codigo === 'SIN_SECUENCIA_E' && puedeUsarB) {
          // Preparar payload para posterior envío
          const payload = {
            ...(tipoOrigen === 'compra'
              ? { id_compra: facturaOriginal.id_compra }
              : { id_factura: facturaOriginal.id_factura }),
            codigo_modificacion: parseInt(formData.codigo_modificacion),
            razon_modificacion: formData.razon_modificacion || null,
            monto: totales.monto,
            impuesto_total: totales.impuesto_total,
            monto_total: totales.monto_total,
            incluir_impuestos: formData.incluir_impuestos,
            comentario: formData.comentario || null,
            clave_idempotencia: crearClaveIdempotenciaFiscal(`nota-${tipoNota}-serie-b`),
            detalles: lineasSeleccionadas.map(linea => ({
              id_producto: linea.id_producto || null,
              descripcion_producto: linea.descripcionNota || linea.descripcion_producto,
              cantidad: linea.cantidadNota,
              precio: linea.precioNota,
              descuento: 0,
              itbis_tasa: formData.incluir_impuestos ? linea.itbis_tasa : 0,
              indicador_facturacion: linea.indicador_facturacion || 1,
              indicador_bien_servicio: linea.indicador_bien_servicio || 2
            }))
          };
          
          setPayloadPendiente(payload);
          setMostrarConfirmacionSerieB(true);
          return;
        }
        
        // No hay secuencias disponibles (ni E ni B) → mensaje claro
        if (codigo === 'SIN_SECUENCIA_E' || codigo === 'SIN_SECUENCIA_B') {
          toast.error(
            '⚠️ No hay secuencias NCF disponibles. Configure una nueva secuencia en Configuración > Secuencias NCF.',
            { autoClose: 8000 }
          );
          return;
        }
      }
      
      const mensaje = error.response?.data?.mensaje || error.response?.data?.detalles || 'Error al crear la nota';
      toast.error(mensaje);
    } finally {
      setCargando(false);
    }
  };

  // Confirmar creación con serie B
  const confirmarCreacionSerieB = async () => {
    setMostrarConfirmacionSerieB(false);
    
    if (!payloadPendiente) return;
    
    setCargando(true);
    try {
      const endpoint = tipoNota === 'credito' 
        ? '/nota_credito_factura' 
        : '/nota_debito_factura';

      const payloadSerieB = {
        ...payloadPendiente,
        usarSerieB: true
      };

      const respuestaB = await instanciaAxios.post(endpoint, payloadSerieB, {
        headers: payloadSerieB?.clave_idempotencia
          ? { 'x-idempotency-key': payloadSerieB.clave_idempotencia }
          : {}
      });

      if (respuestaB.status === 201 || respuestaB.status === 200) {
        const tipoTexto = tipoNota === 'credito' ? 'Crédito' : 'Débito';
        toast.success(`Nota de ${tipoTexto} creada con comprobante físico (serie B)`);
        
        setRespuestaData(respuestaB.data?.data);
        
        if (respuestaB.data?.data?.pdf?.pdf_base64) {
          setPdfBase64(respuestaB.data.data.pdf.pdf_base64);
          setMostrarPDF(true);
        } else {
          onExito(respuestaB.data);
          onClose();
        }
      }
    } catch (errorSerieB) {
      const tipoTexto = tipoNota === 'credito' ? 'Crédito' : 'Débito';
      console.error('Error al crear nota con serie B:', errorSerieB);
      toast.error(errorSerieB.response?.data?.mensaje || `Error al crear Nota de ${tipoTexto} con serie B`);
    } finally {
      setCargando(false);
      setPayloadPendiente(null);
    }
  };

  // Click fuera del modal para cerrar
  const handleClickFuera = (e) => {
    if (e.target === e.currentTarget && !mostrarPDF) {
      onClose();
    }
  };

  // Cerrar modal de PDF
  const handleCerrarPDF = () => {
    setMostrarPDF(false);
    setPdfBase64(null);
    if (respuestaData) {
      onExito({ data: respuestaData });
    }
    onClose();
  };

  if (!mostrar) return null;

  const tituloModal = tipoNota === 'credito' 
    ? 'Crear Nota de Crédito (E34)' 
    : 'Crear Nota de Débito (E33)';

  // Obtener labels dinámicos según tipo de nota y código
  const getLabelColumnas = () => {
    if (tipoNota === 'credito') {
      if (codigoActual === 2) {
        // Código 2: Solo corrige texto, no montos
        return { cantidad: 'Cantidad', monto: 'Monto (no aplica)', accion: 'Nueva Descripción' };
      }
      return { cantidad: 'Cant. Ajuste', monto: 'Monto Ajuste', accion: 'Descripción' };
    }
    // Nota de Débito: "Ajuste" es más apropiado
    return { cantidad: 'Cant. Ajuste', monto: 'Monto Ajuste', accion: 'Descripción' };
  };

  const labels = getLabelColumnas();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleClickFuera}
    >
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] shadow-lg relative flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-color-primario">
          <h2 className="text-xl font-semibold text-white">{tituloModal}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 p-1 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Body con scroll */}
        <div className="flex-1 overflow-y-auto p-6">
          {cargando ? (
            <div className="flex justify-center items-center py-12">
              <SpinnerCargando />
            </div>
          ) : (
            <>
              {/* Seccion: Documento Original */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl border">
                <h3 className="text-sm font-semibold text-color-primario mb-3">
                  {tipoOrigen === 'compra' ? 'Compra Original (E41)' : 'Factura Original'}
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500">NCF</label>
                    <p className="font-mono text-sm font-medium">{facturaOriginal?.ncf || 'Sin NCF'}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">{tipoOrigen === 'compra' ? 'Proveedor' : 'Cliente'}</label>
                    <p className="text-sm truncate">{facturaOriginal?.nombre_cliente || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">{tipoOrigen === 'compra' ? 'Monto Total Compra' : 'Monto Total Factura'}</label>
                    <p className="text-sm font-semibold text-color-primario">{formatoMoneda(facturaOriginal?.monto_total)}</p>
                  </div>
                  {tipoNota === 'credito' && (
                    <div>
                      <label className="block text-xs text-gray-500">Balance Disponible</label>
                      <p className={`text-sm font-semibold ${balanceDisponible > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {formatoMoneda(balanceDisponible)}
                      </p>
                    </div>
                  )}
                </div>
                {tipoNota === 'credito' && balanceDisponible <= 0 && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">⚠️ Este documento no tiene balance disponible para aplicar nota de crédito</p>
                  </div>
                )}
              </div>

              {/* Seccion: Razón de Modificación */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-color-primario mb-3">Razón de Modificación</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Modificación (DGII) *
                    </label>
                    <select
                      value={formData.codigo_modificacion}
                      onChange={(e) => handleFormChange('codigo_modificacion', e.target.value)}
                      className={`w-full border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-color-secundario ${
                        errores.codigo_modificacion ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Seleccione el tipo de modificación</option>
                      {codigosModificacion.map(codigo => (
                        <option key={codigo.codigo} value={codigo.codigo}>
                          {codigo.codigo} - {codigo.descripcion}
                        </option>
                      ))}
                    </select>
                    {errores.codigo_modificacion && (
                      <p className="text-red-500 text-xs mt-1">{errores.codigo_modificacion}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripción adicional
                    </label>
                    <input
                      type="text"
                      value={formData.razon_modificacion}
                      onChange={(e) => handleFormChange('razon_modificacion', e.target.value)}
                      maxLength={90}
                      placeholder="Máx 90 caracteres"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-color-secundario"
                    />
                  </div>
                </div>

                {/* Mensaje informativo según código seleccionado */}
                {codigoActual > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                    <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-700">{comportamiento.descripcion}</p>
                      {comportamiento.autoSeleccionarTodo && (
                        <p className="text-xs text-blue-600 mt-1">
                          <FaLock className="inline mr-1" size={10} />
                          Todas las líneas se seleccionan automáticamente y no son editables
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Opción de impuestos (solo para códigos 1, 3, 4, 5) */}
                {comportamiento.mostrarOpcionImpuestos && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.incluir_impuestos}
                        onChange={(e) => handleFormChange('incluir_impuestos', e.target.checked)}
                        className="w-4 h-4 text-color-secundario rounded focus:ring-color-secundario"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Incluir ITBIS en la nota
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      {formData.incluir_impuestos 
                        ? 'Se calcularán los impuestos sobre el monto de la nota'
                        : 'La nota será solo por el monto base, sin impuestos'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Seccion: Líneas de Ajuste */}
              {codigoActual > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-color-primario">
                      {tipoNota === 'credito' ? 'Líneas a Ajustar' : 'Líneas de la Nota'}
                      {errores.lineas && <span className="text-red-500 text-xs ml-2">({errores.lineas})</span>}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {lineasSeleccionadas.length} de {lineasFactura.length} seleccionadas
                    </span>
                  </div>
                  
                  {lineasFactura.length > 0 ? (
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-color-primario text-white">
                          <tr>
                            <th className="px-3 py-3 w-12">
                              {comportamiento.permitirSeleccion ? (
                                <button
                                  type="button"
                                  onClick={handleSelectAll}
                                  className="text-white hover:text-gray-200"
                                  title={lineasFactura.every(l => l.seleccionado) ? 'Deseleccionar todas' : 'Seleccionar todas'}
                                >
                                  {lineasFactura.every(l => l.seleccionado) 
                                    ? <FaCheckSquare size={18} />
                                    : <FaRegSquare size={18} />
                                  }
                                </button>
                              ) : (
                                <FaLock size={14} className="mx-auto text-white/70" />
                              )}
                            </th>
                            <th className="text-left px-3 py-3">Descripción</th>
                            <th className="text-center px-3 py-3 w-20">Cant. Orig.</th>
                            <th className="text-center px-3 py-3 w-24">{labels.cantidad}</th>
                            <th className="text-right px-3 py-3 w-28">{labels.monto}</th>
                            {!formData.incluir_impuestos ? null : (
                              <th className="text-center px-3 py-3 w-16">ITBIS</th>
                            )}
                            <th className="text-right px-3 py-3 w-28">Total Ajuste</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineasFactura.map((linea, index) => (
                            <tr 
                              key={linea.id} 
                              className={`border-t transition-colors ${
                                linea.seleccionado 
                                  ? 'bg-green-50 hover:bg-green-100' 
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <td className="px-3 py-2 text-center">
                                {comportamiento.permitirSeleccion ? (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleLinea(index)}
                                    className={`${linea.seleccionado ? 'text-color-secundario' : 'text-gray-400'} hover:text-color-secundario`}
                                  >
                                    {linea.seleccionado 
                                      ? <FaCheckSquare size={18} />
                                      : <FaRegSquare size={18} />
                                    }
                                  </button>
                                ) : (
                                  <FaCheckSquare size={18} className="text-color-secundario mx-auto" />
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {comportamiento.permitirEdicion === 'descripcion' && linea.seleccionado ? (
                                  <input
                                    type="text"
                                    value={linea.descripcionNota}
                                    onChange={(e) => handleDescripcionChange(index, e.target.value)}
                                    className="w-full border border-color-secundario rounded-lg px-2 py-1 text-sm bg-white"
                                    placeholder="Nueva descripción"
                                  />
                                ) : (
                                  <span className={linea.seleccionado ? 'font-medium' : 'text-gray-600'}>
                                    {linea.descripcion_producto}
                                  </span>
                                )}
                              </td>
                              <td className="text-center px-3 py-2 text-gray-500">
                                {linea.cantidadOriginal}
                              </td>
                              <td className="px-3 py-2">
                                {comportamiento.permitirEdicion === 'montos' && linea.seleccionado ? (
                                  <input
                                    type="number"
                                    value={linea.cantidadNota}
                                    onChange={(e) => handleCantidadChange(index, e.target.value)}
                                    min="0.01"
                                    max={linea.cantidadOriginal}
                                    step="0.01"
                                    className="w-full border border-color-secundario rounded-lg px-2 py-1 text-sm text-center bg-white"
                                  />
                                ) : (
                                  <span className={`block text-center ${linea.seleccionado ? '' : 'text-gray-400'}`}>
                                    {linea.seleccionado ? linea.cantidadNota : '—'}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {comportamiento.permitirEdicion === 'montos' && linea.seleccionado ? (
                                  <input
                                    type="number"
                                    value={linea.precioNota}
                                    onChange={(e) => handleMontoChange(index, e.target.value)}
                                    min="0"
                                    max={linea.precioOriginal}
                                    step="0.01"
                                    className="w-full border border-color-secundario rounded-lg px-2 py-1 text-sm text-right bg-white"
                                  />
                                ) : (
                                  <span className={`block text-right ${linea.seleccionado ? '' : 'text-gray-400'}`}>
                                    {linea.seleccionado ? formatoMoneda(linea.precioNota) : '—'}
                                  </span>
                                )}
                              </td>
                              {!formData.incluir_impuestos ? null : (
                                <td className="text-center px-3 py-2 text-gray-500">
                                  {linea.itbis_tasa}%
                                </td>
                              )}
                              <td className="text-right px-3 py-2 font-medium">
                                {linea.seleccionado 
                                  ? formatoMoneda(calcularTotalLineaNota(linea))
                                  : '—'
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 py-8 border border-dashed rounded-xl bg-gray-50">
                      Esta factura no tiene líneas de detalle
                    </div>
                  )}
                </div>
              )}

              {/* Seccion: Totales */}
              {codigoActual > 0 && (
                <div className={`mb-6 p-4 rounded-xl border ${excedeMonto ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
                  <div className="flex justify-end">
                    <div className="w-72 space-y-2">
                      <div className="flex justify-between text-gray-600">
                        <span>Subtotal Ajuste:</span>
                        <span className="font-medium">{formatoMoneda(totales.monto)}</span>
                      </div>
                      {formData.incluir_impuestos && (
                        <div className="flex justify-between text-gray-600">
                          <span>ITBIS:</span>
                          <span className="font-medium">{formatoMoneda(totales.impuesto_total)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className={`font-semibold ${excedeMonto ? 'text-red-600' : 'text-color-primario'}`}>
                          Total Nota {tipoNota === 'credito' ? 'Crédito' : 'Débito'}:
                        </span>
                        <span className={`font-bold text-lg ${excedeMonto ? 'text-red-600' : 'text-color-primario'}`}>
                          {formatoMoneda(totales.monto_total)}
                        </span>
                      </div>
                      {tipoNota === 'credito' && (
                        <div className={`flex justify-between text-xs ${excedeMonto ? 'text-red-500' : 'text-gray-500'}`}>
                          <span>Balance disponible:</span>
                          <span>{formatoMoneda(balanceDisponible)}</span>
                        </div>
                      )}
                      {excedeMonto && (
                        <div className="text-xs text-red-600 text-right mt-1">
                          ⚠️ Excede el balance por {formatoMoneda(totales.monto_total - balanceDisponible)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Seccion: Comentario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comentario interno (no se envía a DGII)
                </label>
                <textarea
                  value={formData.comentario}
                  onChange={(e) => handleFormChange('comentario', e.target.value)}
                  rows={2}
                  placeholder="Notas internas..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-color-secundario"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={cargando}
            className="px-6 py-2 rounded-xl bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={cargando || !codigoActual || lineasSeleccionadas.length === 0 || excedeMonto || (tipoNota === 'credito' && !comportamiento.montoCero && balanceDisponible <= 0)}
            className="px-6 py-2 rounded-xl text-white font-medium bg-color-secundario hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cargando ? 'Procesando...' : `Crear Nota de ${tipoNota === 'credito' ? 'Crédito' : 'Débito'}`}
          </button>
        </div>
      </div>

      {/* Modal PDF */}
      <ModalPDFBase64
        mostrar={mostrarPDF}
        onClose={handleCerrarPDF}
        pdfBase64Directo={pdfBase64}
        ncf={respuestaData?.ncf || ''}
        tituloDocumento={tipoNota === 'credito' ? 'Nota de Crédito' : 'Nota de Débito'}
        tipoDocumento="nota_credito"
      />

      {/* Modal de confirmación para usar Serie B */}
      <ModalConfirmacion
        mostrar={mostrarConfirmacionSerieB}
        onConfirmar={confirmarCreacionSerieB}
        onCancelar={() => {
          setMostrarConfirmacionSerieB(false);
          setPayloadPendiente(null);
        }}
        titulo="⚠️ Secuencia Electrónica No Disponible"
        mensaje={`No hay secuencias electrónicas (serie E) disponibles para Nota de ${tipoNota === 'credito' ? 'Crédito' : 'Débito'}.\n\nHay secuencias físicas (serie B) disponibles.\n\n¿Desea crear la nota con comprobante físico (serie B)?`}
        textoConfirmar="Sí, usar Serie B"
        textoCancelar="Cancelar"
        tipo="warning"
      />
    </div>
  );
};

export default ModalCrearNotaFactura;
